/**
 * @fileoverview File Upload Component
 *
 * Robust file upload component using react-dropzone.
 * Features: drag-drop, file previews, ref storage for form submission,
 * cross-browser compatibility, and clear multi-file UX.
 *
 * SSR Exception: MUST use 'use client'
 * Reason: Uses browser APIs (File, DataTransfer) and user interactions.
 */
"use client";

import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { type Accept, type FileRejection, useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";

// Formspree limits
const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB per file
const DEFAULT_MAX_FILES = 10;

export interface FileUploadFile {
	file: File;
	preview: string;
	id: string;
}

export interface FileUploadRef {
	/** Get all currently selected files */
	getFiles: () => File[];
	/** Clear all selected files */
	clear: () => void;
	/** Check if there are any validation errors */
	hasErrors: () => boolean;
}

export interface FileUploadProps {
	/** Field name for form submission */
	name: string;
	/** Accept attribute - use both MIME types and extensions for compatibility */
	accept?: string;
	/** Allow multiple file selection */
	multiple?: boolean;
	/** Maximum number of files (default: 10) */
	maxFiles?: number;
	/** Maximum file size in bytes (default: 25MB) */
	maxSize?: number;
	/** Minimum file size in bytes */
	minSize?: number;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Help text to display */
	helpText?: string;
	/** Callback when files change */
	onChange?: (files: File[]) => void;
	/** Callback when validation error occurs */
	onError?: (error: string | undefined) => void;
	/** External error message */
	error?: string;
	/** Additional CSS classes */
	className?: string;
	/** Aria described by ID */
	"aria-describedby"?: string;
}

/**
 * Parse accept string into react-dropzone Accept format
 * Handles both MIME types and file extensions
 */
function parseAccept(accept?: string): Accept | undefined {
	if (!accept) return undefined;

	// Use mutable arrays during construction, then assign to Accept
	const extensions: string[] = [];
	const mimeTypes: Record<string, string[]> = {};
	const parts = accept.split(",").map((s) => s.trim());

	for (const part of parts) {
		if (part.startsWith(".")) {
			// Collect file extensions
			extensions.push(part);
		} else if (part.includes("/")) {
			// MIME type - initialize with empty extensions array
			mimeTypes[part] = [];
		}
	}

	// Build the Accept object
	const result: Accept = {};

	// Add MIME types
	for (const [mime, exts] of Object.entries(mimeTypes)) {
		result[mime] = exts;
	}

	// Add extensions under a generic MIME type if we have any
	if (extensions.length > 0) {
		// Use application/octet-stream as a catch-all for extensions
		result["application/octet-stream"] = extensions;
	}

	return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Generate a unique ID for a file
 */
function generateFileId(file: File): string {
	return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get human-readable error message for file rejection
 */
function getErrorMessage(rejection: FileRejection): string {
	const { file, errors } = rejection;
	const errorCodes = errors.map((e) => e.code);

	if (errorCodes.includes("file-too-large")) {
		return `"${file.name}" is too large (max ${formatFileSize(DEFAULT_MAX_FILE_SIZE)})`;
	}
	if (errorCodes.includes("file-too-small")) {
		return `"${file.name}" is too small`;
	}
	if (errorCodes.includes("file-invalid-type")) {
		return `"${file.name}" has an unsupported file type`;
	}
	if (errorCodes.includes("too-many-files")) {
		return `Too many files selected`;
	}

	return errors[0]?.message || `"${file.name}" could not be added`;
}

export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
	(
		{
			name,
			accept,
			multiple = false,
			maxFiles = DEFAULT_MAX_FILES,
			maxSize = DEFAULT_MAX_FILE_SIZE,
			minSize,
			required = false,
			disabled = false,
			helpText,
			onChange,
			onError,
			error: externalError,
			className,
			"aria-describedby": ariaDescribedBy,
		},
		ref,
	) => {
		const [files, setFiles] = useState<FileUploadFile[]>([]);
		const [internalError, setInternalError] = useState<string | undefined>();
		const hiddenInputRef = useRef<HTMLInputElement>(null);

		// Store callbacks in refs to avoid infinite loops in useEffect
		// These refs always hold the latest callback without triggering re-renders
		const onChangeRef = useRef(onChange);
		const onErrorRef = useRef(onError);

		// Track if this is the first render to skip initial onChange call
		const isFirstRender = useRef(true);

		// Keep refs in sync with latest props (useLayoutEffect to run before effects)
		useLayoutEffect(() => {
			onChangeRef.current = onChange;
			onErrorRef.current = onError;
		});

		// Expose methods via ref for parent form
		useImperativeHandle(ref, () => ({
			getFiles: () => files.map((f) => f.file),
			clear: () => {
				// Revoke all preview URLs to prevent memory leaks
				for (const f of files) {
					if (f.preview) {
						URL.revokeObjectURL(f.preview);
					}
				}
				setFiles([]);
				setInternalError(undefined);
				if (hiddenInputRef.current) {
					hiddenInputRef.current.value = "";
				}
			},
			hasErrors: () => Boolean(internalError || externalError),
		}));

		// Cleanup preview URLs on unmount
		useEffect(() => {
			return () => {
				for (const f of files) {
					if (f.preview) {
						URL.revokeObjectURL(f.preview);
					}
				}
			};
		}, [files]);

		// Sync files to hidden input for native form submission
		useEffect(() => {
			if (hiddenInputRef.current && files.length > 0) {
				try {
					const dataTransfer = new DataTransfer();
					for (const f of files) {
						dataTransfer.items.add(f.file);
					}
					hiddenInputRef.current.files = dataTransfer.files;
				} catch {
					// DataTransfer not supported in some older browsers
					// Form will use the ref method instead
				}
			} else if (hiddenInputRef.current) {
				hiddenInputRef.current.value = "";
			}
		}, [files]);

		// Notify parent of file changes (skip initial mount to prevent cascading updates)
		// Also skip if files haven't actually changed to prevent unnecessary updates
		const prevFilesRef = useRef<File[]>([]);
		useEffect(() => {
			const currentFiles = files.map((f) => f.file);
			// Skip if this is initial mount or files are the same
			if (isFirstRender.current) {
				isFirstRender.current = false;
				prevFilesRef.current = currentFiles;
				return;
			}
			// Only notify if files actually changed (by reference or length)
			if (
				currentFiles.length === prevFilesRef.current.length &&
				currentFiles.every((f, i) => f === prevFilesRef.current[i])
			) {
				return;
			}
			prevFilesRef.current = currentFiles;
			onChangeRef.current?.(currentFiles);
		}, [files]);

		// Notify parent of errors (only when error actually changes to a non-undefined value
		// or when transitioning from error to no error)
		const prevErrorRef = useRef<string | undefined>(undefined);
		useEffect(() => {
			// Skip if error hasn't actually changed
			if (prevErrorRef.current === internalError) {
				return;
			}
			// Skip initial mount with undefined error (no need to notify parent of nothing)
			if (prevErrorRef.current === undefined && internalError === undefined) {
				prevErrorRef.current = internalError;
				return;
			}
			prevErrorRef.current = internalError;
			onErrorRef.current?.(internalError);
		}, [internalError]);

		const onDrop = useCallback(
			(acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
				// Clear previous errors
				setInternalError(undefined);

				// Handle rejections
				if (rejectedFiles.length > 0) {
					const errorMessages = rejectedFiles
						.slice(0, 3) // Limit error messages
						.map(getErrorMessage);
					const moreCount = rejectedFiles.length - 3;
					if (moreCount > 0) {
						errorMessages.push(`...and ${moreCount} more file(s) rejected`);
					}
					setInternalError(errorMessages.join(". "));
				}

				if (acceptedFiles.length === 0) return;

				// Calculate how many files we can add
				const currentCount = files.length;
				const availableSlots = multiple ? maxFiles - currentCount : 1;

				if (availableSlots <= 0) {
					setInternalError(`Maximum ${maxFiles} files allowed`);
					return;
				}

				const filesToAdd = acceptedFiles.slice(0, availableSlots);
				if (filesToAdd.length < acceptedFiles.length) {
					setInternalError(
						`Only ${filesToAdd.length} of ${acceptedFiles.length} files added (max ${maxFiles})`,
					);
				}

				// Create file entries with previews
				const newFiles: FileUploadFile[] = filesToAdd.map((file) => ({
					file,
					preview: file.type.startsWith("image/")
						? URL.createObjectURL(file)
						: "",
					id: generateFileId(file),
				}));

				if (multiple) {
					setFiles((prev) => [...prev, ...newFiles]);
				} else {
					// Single file mode - replace existing
					for (const f of files) {
						if (f.preview) {
							URL.revokeObjectURL(f.preview);
						}
					}
					setFiles(newFiles);
				}
			},
			[files, maxFiles, multiple],
		);

		const removeFile = useCallback((id: string) => {
			setFiles((prev) => {
				const fileToRemove = prev.find((f) => f.id === id);
				if (fileToRemove?.preview) {
					URL.revokeObjectURL(fileToRemove.preview);
				}
				return prev.filter((f) => f.id !== id);
			});
			setInternalError(undefined);
		}, []);

		const {
			getRootProps,
			getInputProps,
			isDragActive,
			isDragAccept,
			isDragReject,
		} = useDropzone({
			onDrop,
			accept: parseAccept(accept),
			multiple,
			maxFiles: multiple ? maxFiles : 1,
			maxSize,
			minSize,
			disabled,
			noClick: false,
			noKeyboard: false,
		});

		const error = externalError || internalError;
		const hasFiles = files.length > 0;
		const effectiveMaxFiles = multiple ? maxFiles : 1;

		return (
			<div className={cn("space-y-3", className)}>
				{/* Hidden input for native form submission */}
				<input
					ref={hiddenInputRef}
					type="file"
					name={name}
					multiple={multiple}
					required={required && files.length === 0}
					style={{ display: "none" }}
					tabIndex={-1}
					aria-hidden="true"
				/>

				{/* Dropzone */}
				<div
					{...getRootProps({
						className: cn(
							"relative rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
							isDragActive && "border-primary bg-primary/5",
							isDragAccept && "border-primary bg-primary/10",
							isDragReject && "border-destructive bg-destructive/5",
							!isDragActive &&
								!error &&
								"border-input hover:border-primary/50 hover:bg-muted/50",
							error && "border-destructive",
							disabled && "cursor-not-allowed opacity-50",
						),
					})}
					aria-describedby={ariaDescribedBy}
					aria-invalid={Boolean(error)}
				>
					<input {...getInputProps()} />

					<div className="flex flex-col items-center justify-center gap-2 text-center">
						{/* Upload Icon */}
						<div
							className={cn(
								"rounded-full p-3",
								isDragAccept && "bg-primary/20 text-foreground",
								isDragReject && "bg-destructive/10 text-destructive",
								!isDragActive && "bg-muted text-muted-foreground",
							)}
						>
							<svg
								className="h-6 w-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								/>
							</svg>
						</div>

						{/* Instructions */}
						{isDragActive ? (
							<p className="text-sm font-medium">
								{isDragAccept
									? "Drop files here"
									: "Some files will be rejected"}
							</p>
						) : (
							<>
								<p className="text-sm font-medium">
									Drag and drop {multiple ? "files" : "a file"} here, or click
									to select
								</p>
								<p className="text-xs text-muted-foreground">
									{multiple
										? `Up to ${effectiveMaxFiles} files, max ${formatFileSize(maxSize)} each`
										: `Max ${formatFileSize(maxSize)}`}
								</p>
							</>
						)}
					</div>
				</div>

				{/* Help text */}
				{helpText && !error && (
					<p className="text-sm text-muted-foreground">{helpText}</p>
				)}

				{/* Error message */}
				{error && (
					<p className="text-sm text-destructive" role="alert">
						{error}
					</p>
				)}

				{/* File list */}
				{hasFiles && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-foreground">
							{files.length} {files.length === 1 ? "file" : "files"} selected
						</p>
						<ul className="space-y-2">
							{files.map((fileEntry) => (
								<li
									key={fileEntry.id}
									className="flex items-center gap-3 rounded-lg border bg-muted/30 p-2"
								>
									{/* Thumbnail preview for images - uses native img because next/image doesn't support blob URLs */}
									{fileEntry.preview ? (
										<div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
											{/* biome-ignore lint/performance/noImgElement: next/image doesn't support blob URLs from URL.createObjectURL() */}
											<img
												src={fileEntry.preview}
												alt={`Preview of ${fileEntry.file.name}`}
												className="h-full w-full object-cover"
											/>
										</div>
									) : (
										<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-muted">
											<svg
												className="h-5 w-5 text-muted-foreground"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
												/>
											</svg>
										</div>
									)}

									{/* File info */}
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-medium">
											{fileEntry.file.name}
										</p>
										<p className="text-xs text-muted-foreground">
											{formatFileSize(fileEntry.file.size)}
										</p>
									</div>

									{/* Remove button */}
									<button
										type="button"
										onClick={() => removeFile(fileEntry.id)}
										className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										aria-label={`Remove ${fileEntry.file.name}`}
									>
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	},
);

FileUpload.displayName = "FileUpload";
